import React, { ReactNode } from 'react';
import { Box, Grid, Link, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { showAlert } from '../../../features/slices/applicationSlice';
import EstablecimientoVisitasInfo from './EstablecimientoVisitasInfo';

interface Establishment {
    id: number;
    nombre: string;
    localidad: any;
    postalCode: string;
    estado: number;
    direccion: any;
    // N° de establecimiento (en el back se llama codigoSRT, ver nroEstablecimiento en el dominio)
    codigoSRT?: string | number | null;
}

interface Props {
    establishments: Establishment[];
    selectedEstablishments: number[];
    selectedEstablishmentsData?: Establishment[];
    onRemove?: (id: number) => void;
    onRemoveMultiple?: (ids: number[]) => void;
    title?: ReactNode;
    visitasPorEstablecimiento?: Record<number, any[]>;
    isLoadingVisitas?: boolean;
    establecimientosIncompatibles?: Set<number>;
    tiposVisitaIncompatibles?: Set<number>;
    haySeleccionTipoVisita?: boolean;
}

const EstablecimientosSeleccionados: React.FC<Props> = ({
    establishments,
    selectedEstablishments,
    selectedEstablishmentsData,
    onRemove,
    onRemoveMultiple,
    title,
    visitasPorEstablecimiento,
    isLoadingVisitas,
    establecimientosIncompatibles,
    tiposVisitaIncompatibles,
    haySeleccionTipoVisita
}) => {
    const { t } = useTranslation('visitas');
    const dispatch = useAppDispatch();

    const selectedItemsSinOrdenar =
        selectedEstablishmentsData && selectedEstablishmentsData.length > 0
            ? selectedEstablishmentsData
            : establishments.filter((est) => selectedEstablishments.includes(est.id));

    // Los establecimientos incompatibles con el tipo de visita seleccionado se listan primero
    // para que el usuario los identifique sin tener que recorrer toda la lista.
    const selectedItems = establecimientosIncompatibles?.size
        ? [...selectedItemsSinOrdenar].sort((a, b) => {
              const aIncompatible = establecimientosIncompatibles.has(a.id) ? 0 : 1;
              const bIncompatible = establecimientosIncompatibles.has(b.id) ? 0 : 1;
              return aIncompatible - bIncompatible;
          })
        : selectedItemsSinOrdenar;

    const establecimientosConConflicto = selectedItems.filter((est) =>
        establecimientosIncompatibles?.has(est.id)
    );

    const handleRemoveClick = (establecimiento: Establishment) => {
        dispatch(
            showAlert({
                title: t('alerts.removeSelectedEstablishment.title', {
                    nombre: establecimiento.nombre
                }),
                confirmAction: () => onRemove?.(establecimiento.id),
                confirmText: t('alerts.removeSelectedEstablishment.confirmText'),
                cancelText: t('alerts.removeSelectedEstablishment.cancelText'),
                icon: 'info'
            })
        );
    };

    const handleRemoveAllIncompatibleClick = () => {
        const ids = establecimientosConConflicto.map((est) => est.id);
        dispatch(
            showAlert({
                title: t('alerts.removeAllIncompatibleEstablishments.title', {
                    count: ids.length
                }),
                confirmAction: () => onRemoveMultiple?.(ids),
                confirmText: t('alerts.removeAllIncompatibleEstablishments.confirmText'),
                cancelText: t('alerts.removeAllIncompatibleEstablishments.cancelText'),
                icon: 'warning'
            })
        );
    };

    const formatDireccion = (direccion?: any): string => {
        if (!direccion) return 'N/A';

        const { calle, altura, departamento } = direccion;
        const calleYAltura = calle && altura ? `${calle} ${altura}` : calle || altura || '';
        const dpto = departamento ? ` - Dpto: ${departamento}` : '';

        return calleYAltura + dpto || 'N/A';
    };

    const formatText = (value: any): string => {
        if (typeof value === 'string' && value.trim()) return value;
        if (typeof value === 'number') return String(value);
        if (value?.nombre) return value.nombre;
        if (value?.codigoPostal) return String(value.codigoPostal);

        return 'N/A';
    };

    // A diferencia del resto de los datos de la card, el N° sin informar se muestra como "-" (SMP-669)
    const formatNroEstablecimiento = (value?: string | number | null): string =>
        value !== undefined && value !== null && String(value).trim() !== ''
            ? String(value).trim()
            : '-';

    const getLocality = (establishment: Establishment) =>
        establishment.direccion?.localidad || establishment.localidad;

    const getProvince = (establishment: Establishment) =>
        establishment.direccion?.localidad?.provincia || establishment.localidad?.provincia;

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, color: '#384A52' }}>
                {title || t('newVisit.selectedEstablishments')}
            </Typography>

            {onRemoveMultiple && establecimientosConConflicto.length > 0 && (
                <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleRemoveAllIncompatibleClick}
                    sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'warning.dark' }}
                >
                    {t('newVisit.removeAllIncompatibleLink')}
                </Link>
            )}

            {/* ~3,5 cards visibles (cada card mide ~187px + 12px de margen); el resto se ve con scroll */}
            <Box sx={{ maxHeight: 720, overflowY: 'auto', width: '100%' }}>
                {selectedItems.length === 0 ? (
                    <Box
                        sx={{
                            borderRadius: 1,
                            bgcolor: '#fff',
                            p: 2,
                            boxShadow: '-1px 1px 4px rgba(0, 0, 0, 0.10)'
                        }}
                    >
                        <Typography variant="body2">
                            {t('table.noData.noSelectedEstablishments')}
                        </Typography>
                    </Box>
                ) : (
                    selectedItems.map((est) => {
                        const esIncompatible = establecimientosIncompatibles?.has(est.id);

                        return (
                        <Box
                            key={est.id}
                            sx={{
                                width: '100%',
                                borderRadius: 1,
                                bgcolor: '#fff',
                                p: 2,
                                mb: 1.5,
                                boxShadow: '-1px 1px 4px rgba(0, 0, 0, 0.10)',
                                ...(esIncompatible && {
                                    border: '3px solid',
                                    borderColor: 'warning.main'
                                })
                            }}
                        >
                            <Grid container alignItems="center" sx={{ width: '100%' }}>
                                <Grid item xs={11}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontSize: 13, fontWeight: 700 }}
                                    >
                                        {est.nombre}
                                    </Typography>
                                </Grid>

                                {onRemove && (
                                    <Grid
                                        item
                                        xs={1}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end'
                                        }}
                                    >
                                        <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleRemoveClick(est)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Grid>
                                )}
                            </Grid>

                            <Box sx={{ pl: 0.5, mt: 0.5 }}>
                                <Typography variant="body2" color="textSecondary">
                                    {`N° de establecimiento: ${formatNroEstablecimiento(est.codigoSRT)}`}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {`Dirección: ${formatDireccion(est.direccion)}`}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {`Localidad: ${formatText(getLocality(est))} (${formatText(
                                        getLocality(est)?.codigoPostal || est.postalCode
                                    )})`}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {`Provincia: ${formatText(getProvince(est))}`}
                                </Typography>
                            </Box>

                            <EstablecimientoVisitasInfo
                                visitas={visitasPorEstablecimiento?.[est.id] || []}
                                loading={isLoadingVisitas}
                                defaultExpanded={esIncompatible}
                                tiposVisitaIncompatibles={tiposVisitaIncompatibles}
                                tieneConflicto={esIncompatible}
                                haySeleccionTipoVisita={haySeleccionTipoVisita}
                            />
                        </Box>
                        );
                    })
                )}
            </Box>
        </Box>
    );
};

export default EstablecimientosSeleccionados;
